/** Fingerprint sign-in (passkeys / WebAuthn): browser helpers, the sign-in button, and the setup card. */
import { useEffect, useState } from 'react';
import { Icon } from './icons.tsx';
import { api } from './api.ts';
import { Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from './ui.tsx';

const toBuf = (s: string) => {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  return Uint8Array.from(b, (c) => c.charCodeAt(0)).buffer;
};
const toB64u = (b: ArrayBuffer | null) => {
  if (!b) return null;
  let s = '';
  for (const x of new Uint8Array(b)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeysSupported = () => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

/** True when this device has a built-in fingerprint/face reader (or Windows Hello PIN) ready to use. */
export function usePlatformAuthenticator() {
  const [ok, setOk] = useState<boolean | null>(null);
  useEffect(() => {
    if (!passkeysSupported()) return setOk(false);
    PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()
      .then(setOk)
      .catch(() => setOk(false));
  }, []);
  return ok;
}

/** Turn the browser's terse errors into something a deputy can act on. */
function friendly(e: unknown): Error {
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError') return new Error('Fingerprint sign-in was cancelled or timed out.');
  if (name === 'InvalidStateError') return new Error('This device already has a CivilHound fingerprint sign-in for you.');
  if (name === 'SecurityError') return new Error('Fingerprint sign-in only works on the real CivilHound address (https).');
  if (name === 'NotSupportedError') return new Error('This device or browser can’t do fingerprint sign-in.');
  return e as Error;
}

/** Ask the device for a fingerprint and sign in. With expectUserId, only that user (lock-screen unlock). */
export async function signInWithPasskey(expectUserId?: string) {
  const o = await api.post<{ challengeId: string; publicKey: any }>('/api/auth/passkey/login-options');
  let cred: PublicKeyCredential;
  try {
    cred = (await navigator.credentials.get({
      publicKey: { ...o.publicKey, challenge: toBuf(o.publicKey.challenge), allowCredentials: [] },
    })) as PublicKeyCredential;
  } catch (e) {
    throw friendly(e);
  }
  const r = cred.response as AuthenticatorAssertionResponse;
  const res = await api.post<{ agencyCode: string; username: string }>('/api/auth/passkey/login', {
    challengeId: o.challengeId,
    expectUserId,
    credential: {
      id: cred.id,
      rawId: toB64u(cred.rawId),
      type: cred.type,
      response: { clientDataJSON: toB64u(r.clientDataJSON), authenticatorData: toB64u(r.authenticatorData), signature: toB64u(r.signature), userHandle: toB64u(r.userHandle) },
    },
  });
  try {
    localStorage.setItem('civilos.agency', res.agencyCode);
  } catch {
    /* ignore */
  }
  return res;
}

/**
 * Confirm one action (signing or notarizing an affidavit) with the fingerprint. Returns the proof to send along
 * with that request; the server checks it belongs to you and to exactly this action.
 */
export async function confirmWithFingerprint(action: string) {
  const o = await api.post<{ challengeId: string; publicKey: any }>('/api/auth/passkey/confirm-options', { action });
  let cred: PublicKeyCredential;
  try {
    cred = (await navigator.credentials.get({
      publicKey: {
        ...o.publicKey,
        challenge: toBuf(o.publicKey.challenge),
        allowCredentials: o.publicKey.allowCredentials.map((c: { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }) => ({ ...c, id: toBuf(c.id) })),
      },
    })) as PublicKeyCredential;
  } catch (e) {
    throw friendly(e);
  }
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    challengeId: o.challengeId,
    credential: {
      id: cred.id,
      rawId: toB64u(cred.rawId),
      type: cred.type,
      response: { clientDataJSON: toB64u(r.clientDataJSON), authenticatorData: toB64u(r.authenticatorData), signature: toB64u(r.signature), userHandle: toB64u(r.userHandle) },
    },
  };
}

/** Whether I have fingerprint sign-in set up (on any device). */
export function useHasPasskey() {
  const [has, setHas] = useState<boolean | null>(null);
  useEffect(() => {
    if (!passkeysSupported()) return setHas(false);
    api
      .get<{ passkeys: unknown[] }>('/api/auth/passkeys')
      .then((r) => setHas(r.passkeys.length > 0))
      .catch(() => setHas(false));
  }, []);
  return has;
}

async function createPasskey(label: string) {
  const o = await api.post<{ challengeId: string; publicKey: any }>('/api/auth/passkeys/options');
  const pk = o.publicKey;
  let cred: PublicKeyCredential;
  try {
    cred = (await navigator.credentials.create({
      publicKey: {
        ...pk,
        challenge: toBuf(pk.challenge),
        user: { ...pk.user, id: toBuf(pk.user.id) },
        excludeCredentials: pk.excludeCredentials.map((c: { id: string; type: 'public-key' }) => ({ ...c, id: toBuf(c.id) })),
      },
    })) as PublicKeyCredential;
  } catch (e) {
    throw friendly(e);
  }
  const r = cred.response as AuthenticatorAttestationResponse;
  return api.post('/api/auth/passkeys', {
    challengeId: o.challengeId,
    label,
    credential: { id: cred.id, rawId: toB64u(cred.rawId), type: cred.type, response: { clientDataJSON: toB64u(r.clientDataJSON), attestationObject: toB64u(r.attestationObject), transports: r.getTransports?.() ?? [] } },
  });
}

/** “Sign in with fingerprint” for the sign-in page. Hidden on devices that can't do it. */
export function PasskeySignInButton({ onSignedIn }: { onSignedIn: () => Promise<void> | void }) {
  const a = useAction();
  if (!passkeysSupported()) return null;
  return (
    <div className="stack pk-signin">
      <div className="or-line small muted">
        <span>or</span>
      </div>
      <button type="button" className="btn pk-btn" disabled={a.busy} onClick={() => a.run(async () => (await signInWithPasskey(), await onSignedIn()))}>
        <Icon name="fingerprint" size={18} /> {a.busy ? 'Touch the fingerprint reader…' : 'Fingerprint sign-in'}
      </button>
      <ErrorBox error={a.error} />
    </div>
  );
}

const guessLabel = () => {
  const ua = navigator.userAgent;
  if (/Windows/.test(ua)) return 'Windows laptop (Windows Hello)';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android phone';
  if (/Mac/.test(ua)) return 'Mac (Touch ID)';
  return 'This device';
};
const when = (s: string | null) => (s ? new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never');

/** Set up / list / remove fingerprint sign-in. */
export function PasskeysCard() {
  const list = useLoad(() => api.get<{ passkeys: { id: string; label: string; created_at: string; last_used_at: string | null }[] }>('/api/auth/passkeys'), []);
  const platform = usePlatformAuthenticator();
  const [label, setLabel] = useState(guessLabel);
  const [done, setDone] = useState(false);
  const a = useAction();
  return (
    <Card title="Fingerprint sign-in" className="pk-card">
      <p className="small muted">
        Sign in and unlock with the fingerprint reader instead of your password and authenticator code. Your fingerprint never leaves this device — CivilHound only gets a key the device makes for this site.
      </p>
      {!list.data ? (
        list.error ? <ErrorBox error={list.error} /> : <Spinner />
      ) : list.data.passkeys.length === 0 ? (
        <p className="small">Not set up yet.</p>
      ) : (
        <ul className="plain pk-list">
          {list.data.passkeys.map((p) => (
            <li key={p.id} className="row between">
              <span>
                <strong><Icon name="fingerprint" size={15} /> {p.label}</strong>
                <br />
                <span className="small muted">
                  Set up {when(p.created_at)} · last used {when(p.last_used_at)}
                </span>
              </span>
              <button
                className="btn sm ghost"
                disabled={a.busy}
                onClick={() => window.confirm(`Remove fingerprint sign-in for “${p.label}”?`) && a.run(async () => (await api.post(`/api/auth/passkeys/${p.id}/remove`), list.reload()))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {!passkeysSupported() ? (
        <Notice tone="warn">This browser can’t do fingerprint sign-in. Use Chrome or Edge on the laptop, or Safari on an iPhone.</Notice>
      ) : (
        <div className="stack">
          {platform === false && (
            <Notice tone="warn">
              No fingerprint reader is set up on this device yet. On Windows: <strong>Settings → Accounts → Sign-in options → Fingerprint recognition (Windows Hello)</strong>, then come back here.
            </Notice>
          )}
          <Field label="Name for this device">
            <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} />
          </Field>
          <div className="row gap wrap">
            <button className="btn primary" disabled={a.busy || !label.trim()} onClick={() => a.run(async () => (await createPasskey(label.trim()), setDone(true), list.reload()))}>
              {a.busy ? 'Touch the fingerprint reader…' : 'Set up fingerprint on this device'}
            </button>
          </div>
          {done && <Notice tone="ok">Done. Next time, choose “Fingerprint sign-in” on the sign-in page — or unlock the lock screen with it.</Notice>}
          <ErrorBox error={a.error} />
        </div>
      )}
    </Card>
  );
}
